import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import DefaultLayout from 'ui/layouts/DefaultLayout';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import { NoAccessNotice } from 'ui/components/NoAccessNotice';
import { PageLinks } from 'ui/components/listings/PageLinks';
import { EventsPageHeader } from 'ui/bw-ui/events/EventsPageHeader';
import { EventsPageList } from 'ui/_pages/events/EventsPageList';
import { EventsPageZeroCase } from 'ui/_pages/events/EventsPageZeroCase';
import FeaturedEventsCarousel from 'ui/bw-ui/events/FeaturedEventsCarousel';
import {
  useAllEventsQuery,
  isNotGymClass,
  isFeatured,
  inCategory,
  matchesSearch,
} from 'ui/bw-ui/hooks/useEvents';
import { parseIdParam, parseSearchParam } from '@portal/env/utils/paginate';
import { useUrlPagination } from '@portal/ui/hooks/useUrlPagination';
import { useMobxStores } from '@portal/env/hooks/useMobxStores';

const PAGE_SIZE = 12;

function EventsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const { appStore } = useMobxStores();
  const isPastEvent = router.query.pastEvents === 'true';
  const { data, isLoading, error } = useAllEventsQuery({ past: isPastEvent });

  const categoryId = parseIdParam(router.query.categoryId);
  const searchQuery = parseSearchParam(router.query.query);

  const visible = (data?.events ?? []).filter(isNotGymClass);
  const matching = visible
    .filter(inCategory(categoryId))
    .filter(matchesSearch(searchQuery));

  const { items, page, totalPages } = useUrlPagination(matching, PAGE_SIZE);

  const isFiltered = !!searchQuery || categoryId !== null;
  const featuredEvents = isFiltered
    ? []
    : visible.filter(isFeatured).slice(0, 3);

  return (
    <DefaultLayout title={t('Events - My Account - Bruntwood')}>
      <main className="content content--custom" role="main">
        <div
          className={`container ${appStore.mainMenuDocked ? 'tw:lg:pr-8' : ''}`}
        >
          <EventsPageHeader categories={data?.categories ?? []} />
          {isLoading && <LoadingSpinner />}
          <NoAccessNotice
            error={
              error
                ? t("You currently don't have access to the events calendar.")
                : null
            }
          />
          {data && (
            <>
              <FeaturedEventsCarousel
                events={featuredEvents}
                pastEvents={data.pastEvents}
              />
              <EventsPageList events={items} />
              {matching.length === 0 && (
                <EventsPageZeroCase pastEvents={data.pastEvents} />
              )}
              <PageLinks page={page} totalPages={totalPages} />
            </>
          )}
        </div>
      </main>
    </DefaultLayout>
  );
}

export default observer(EventsPage);
